import ELK from "elkjs/lib/elk.bundled.js";
import { NODE_WIDTH, NODE_HEIGHT, GATEWAY_SIZE, GATEWAY_NODE_HEIGHT, START_END_WIDTH, START_END_HEIGHT, LAYER_SPACING, SWIMLANE_LABEL_WIDTH, SWIMLANE_MIN_HEIGHT, SWIMLANE_PADDING_X, SWIMLANE_PADDING_Y, SWIMLANE_GAP } from "./constants";
import { VIRAL_PLACEHOLDER_WIDTH, VIRAL_PLACEHOLDER_HEIGHT } from "./nodes/ViralPlaceholderNode";
const NODE_STACK_GAP = 120; // Increased to give more room for edges between stacked nodes
// ELK instance (singleton for performance)
const elk = new ELK();
/**
 * Get the width and height of a node based on its type
 */ export function getNodeDimensions(node) {
    if (node.type === "flowStart" || node.type === "flowEnd") {
        return {
            width: START_END_WIDTH,
            height: START_END_HEIGHT
        };
    }
    if (node.type === "flowGateway") {
        return {
            width: GATEWAY_SIZE,
            height: GATEWAY_NODE_HEIGHT
        };
    }
    if (node.type === "flowViralPlaceholder") {
        return {
            width: VIRAL_PLACEHOLDER_WIDTH,
            height: VIRAL_PLACEHOLDER_HEIGHT
        };
    }
    return {
        width: NODE_WIDTH,
        height: NODE_HEIGHT
    };
}
/**
 * Dimensions used for helper-line alignment. Differs from `getNodeDimensions`
 * only for gateways: the gateway's bbox reserves 30px below the diamond for
 * its label, so using the bbox center would land the horizontal center guide
 * in the label area. Returning the 60×60 diamond box puts the guide on the
 * diamond's visual center instead.
 */ export function getHelperLineDimensions(node) {
    if (node.type === "flowGateway") {
        return {
            width: GATEWAY_SIZE,
            height: GATEWAY_SIZE
        };
    }
    return getNodeDimensions(node);
}
/**
 * Partition nodes into top-level (no swimlane) and swimlane children
 */ function partitionNodesBySwimlane(nodes, nodeSwimlaneMap) {
    const topLevelNodes = [];
    const swimlaneChildNodes = [];
    nodes.forEach((node)=>{
        if (!nodeSwimlaneMap.has(node.id)) {
            topLevelNodes.push(node);
        } else {
            swimlaneChildNodes.push(node);
        }
    });
    return {
        topLevelNodes,
        swimlaneChildNodes
    };
}
/**
 * Run ELK layout and extract node positions
 */ async function computeElkPositions(nodes, edges) {
    const elkNodes = nodes.map((node)=>{
        const { width, height } = getNodeDimensions(node);
        return {
            id: node.id,
            width,
            height
        };
    });
    const elkEdges = edges.map((edge)=>({
            id: edge.id,
            sources: [
                edge.source
            ],
            targets: [
                edge.target
            ]
        }));
    const elkGraph = {
        id: "root",
        layoutOptions: {
            "elk.algorithm": "layered",
            "elk.direction": "RIGHT",
            "elk.layered.spacing.nodeNodeBetweenLayers": String(LAYER_SPACING),
            "elk.layered.spacing.nodeNode": "120",
            "elk.spacing.nodeNode": "60",
            "elk.edgeRouting": "ORTHOGONAL",
            "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
            "elk.layered.crossingMinimization.greedySwitch.type": "TWO_SIDED",
            "elk.layered.nodePlacement.strategy": "BRANDES_KOEPF",
            "elk.layered.nodePlacement.favorStraightEdges": "true",
            "elk.layered.considerModelOrder.strategy": "PREFER_EDGES"
        },
        children: elkNodes,
        edges: elkEdges
    };
    const layoutedGraph = await elk.layout(elkGraph);
    const elkPositions = new Map();
    layoutedGraph.children?.forEach((elkNode)=>{
        elkPositions.set(elkNode.id, {
            x: elkNode.x || 0,
            y: elkNode.y || 0,
            layer: Math.round((elkNode.x || 0) / LAYER_SPACING)
        });
    });
    return elkPositions;
}
/**
 * Group swimlane child nodes by their lane key
 */ function groupNodesByLane(swimlaneChildNodes, nodeSwimlaneMap, swimlaneOrder) {
    const laneGroups = new Map();
    swimlaneOrder.forEach((info)=>{
        laneGroups.set(info.key, []);
    });
    swimlaneChildNodes.forEach((node)=>{
        const laneKey = nodeSwimlaneMap.get(node.id);
        if (!laneKey) return;
        if (!laneGroups.has(laneKey)) {
            laneGroups.set(laneKey, []);
        }
        laneGroups.get(laneKey)?.push(node);
    });
    return laneGroups;
}
/**
 * Compute the global X extent (min/max) across all swimlane child nodes
 */ function computeGlobalXExtent(swimlaneChildNodes, elkPositions) {
    let globalMinX = Infinity;
    let globalMaxX = -Infinity;
    swimlaneChildNodes.forEach((node)=>{
        const pos = elkPositions.get(node.id);
        if (!pos) return;
        const { width } = getNodeDimensions(node);
        globalMinX = Math.min(globalMinX, pos.x);
        globalMaxX = Math.max(globalMaxX, pos.x + width);
    });
    return {
        globalMinX,
        globalMaxX
    };
}
/**
 * Compute dimensions (height, Y offset) for each swimlane
 */ function computeLaneDimensions(swimlaneOrder, laneGroups, elkPositions) {
    const laneDimensions = [];
    let cumulativeY = 0;
    swimlaneOrder.forEach((info)=>{
        const laneNodes = laneGroups.get(info.key) || [];
        if (laneNodes.length === 0) return;
        const layerGroups = groupByElkLayer(laneNodes, elkPositions);
        let maxStackHeight = 0;
        layerGroups.forEach((nodesInLayer)=>{
            let stackHeight = 0;
            nodesInLayer.forEach((node)=>{
                stackHeight += getNodeDimensions(node).height;
            });
            stackHeight += (nodesInLayer.length - 1) * NODE_STACK_GAP;
            maxStackHeight = Math.max(maxStackHeight, stackHeight);
        });
        const laneHeight = Math.max(SWIMLANE_MIN_HEIGHT, maxStackHeight + SWIMLANE_PADDING_Y * 2);
        laneDimensions.push({
            info,
            yOffset: cumulativeY,
            height: laneHeight,
            nodesInLane: laneNodes
        });
        cumulativeY += laneHeight + SWIMLANE_GAP;
    });
    return laneDimensions;
}
/**
 * Build synchronous placeholder swimlane container nodes plus children with
 * `parentId` assigned. Dimensions are placeholders — actual dims come from
 * either `applySwimlaneLayout` (ELK) or `applyLaneLayout` (sync, driven by
 * child base coordinates).
 */ export function buildLanePlaceholders(nodes, nodeSwimlaneMap, swimlaneOrder, laneExtras) {
    const { topLevelNodes, swimlaneChildNodes } = partitionNodesBySwimlane(nodes, nodeSwimlaneMap);
    const childrenWithParentId = swimlaneChildNodes.map((node)=>({
            ...node,
            parentId: `swimlane-${nodeSwimlaneMap.get(node.id)}`
        }));
    const swimlaneNodes = swimlaneOrder.map((info)=>{
        const extras = laneExtras?.get(info.key);
        return {
            id: `swimlane-${info.key}`,
            type: "flowSwimlane",
            position: {
                x: 0,
                y: 0
            },
            data: {
                canvasKind: "swimlane",
                label: info.label,
                laneType: info.laneType,
                isDeleted: info.isDeleted,
                color: info.color,
                width: MIN_SWIMLANE_WIDTH,
                height: SWIMLANE_MIN_HEIGHT,
                laneKey: info.key,
                onMoveUp: extras?.onMoveUp,
                onMoveDown: extras?.onMoveDown
            },
            selectable: false,
            draggable: false,
            style: {
                zIndex: -1,
                width: MIN_SWIMLANE_WIDTH,
                height: SWIMLANE_MIN_HEIGHT
            }
        };
    });
    return {
        swimlaneNodes,
        childrenWithParentId,
        topLevelNodes
    };
}
const MIN_SWIMLANE_WIDTH = SWIMLANE_LABEL_WIDTH + SWIMLANE_PADDING_X;
/**
 * Position child nodes within their swimlane containers
 */ function positionChildNodes(laneDimensions, elkPositions, contentStartX, globalMinX) {
    const positionedNodes = [];
    laneDimensions.forEach((lane)=>{
        const layerGroups = groupByElkLayer(lane.nodesInLane, elkPositions);
        layerGroups.forEach((nodesInLayer)=>{
            const sortedNodes = [
                ...nodesInLayer
            ].sort((a, b)=>{
                const posA = elkPositions.get(a.id);
                const posB = elkPositions.get(b.id);
                return (posA?.y || 0) - (posB?.y || 0);
            });
            let totalStackHeight = 0;
            sortedNodes.forEach((node)=>{
                totalStackHeight += getNodeDimensions(node).height;
            });
            totalStackHeight += (sortedNodes.length - 1) * NODE_STACK_GAP;
            let sumX = 0;
            sortedNodes.forEach((node)=>{
                const pos = elkPositions.get(node.id);
                sumX += pos?.x || 0;
            });
            const layerX = sumX / sortedNodes.length;
            let currentY = (lane.height - totalStackHeight) / 2;
            sortedNodes.forEach((node)=>{
                const { height } = getNodeDimensions(node);
                positionedNodes.push({
                    ...node,
                    position: {
                        x: contentStartX + (layerX - globalMinX),
                        y: currentY
                    },
                    parentId: `swimlane-${lane.info.key}`
                });
                currentY += height + NODE_STACK_GAP;
            });
        });
    });
    return positionedNodes;
}
/**
 * Apply swimlane layout using ELK for X positions (horizontal rank/layer),
 * then assign Y positions per swimlane band.
 *
 * Input is a pre-assembled graph (from `assembleProcessFlow`): swimlane
 * containers already exist as placeholder nodes, children already carry
 * `parentId`. This function runs ELK and overwrites positions + swimlane
 * dimensions on the existing nodes.
 *
 * Strategy:
 * 1. Run ELK on the flat graph → gives us X positions (horizontal layers) with good edge routing
 * 2. Each node keeps its ELK X position
 * 3. Y position is determined by which swimlane the node belongs to
 * 4. Within a lane, nodes at the same X-layer are stacked vertically
 * 5. Swimlane group nodes wrap child nodes visually
 */ export async function applySwimlaneLayout(assembly) {
    const { nodes, edges, nodeSwimlaneMap, swimlaneOrder } = assembly;
    // Step 1: Partition by node shape — swimlane containers, their children, and top-level loose nodes.
    const swimlaneParents = [];
    const swimlaneChildNodes = [];
    const topLevelNodes = [];
    for (const node of nodes){
        if (node.type === "flowSwimlane") {
            swimlaneParents.push(node);
        } else if (node.parentId) {
            swimlaneChildNodes.push(node);
        } else {
            topLevelNodes.push(node);
        }
    }
    if (swimlaneChildNodes.length === 0) {
        return applySimpleElkLayout(topLevelNodes, edges);
    }
    // Step 2: Run ELK layout on the flat graph (excluding swimlane containers).
    const elkPositions = await computeElkPositions([
        ...topLevelNodes,
        ...swimlaneChildNodes
    ], edges);
    // Step 3: Group children by swimlane.
    const laneGroups = groupNodesByLane(swimlaneChildNodes, nodeSwimlaneMap, swimlaneOrder);
    // Step 4: Compute X extent for uniform swimlane width.
    const { globalMinX, globalMaxX } = computeGlobalXExtent(swimlaneChildNodes, elkPositions);
    const contentStartX = SWIMLANE_LABEL_WIDTH + SWIMLANE_PADDING_X;
    const swimlaneContentWidth = globalMaxX - globalMinX + SWIMLANE_PADDING_X * 2;
    const swimlaneWidth = SWIMLANE_LABEL_WIDTH + swimlaneContentWidth;
    // Step 5: Compute lane dimensions.
    const laneDimensions = computeLaneDimensions(swimlaneOrder, laneGroups, elkPositions);
    const totalSwimlaneHeight = laneDimensions.length > 0 ? laneDimensions[laneDimensions.length - 1].yOffset + laneDimensions[laneDimensions.length - 1].height : 1;
    // Step 6: Overwrite swimlane container positions + dimensions on the pre-existing placeholders.
    const laneMetrics = new Map();
    laneDimensions.forEach((lane)=>laneMetrics.set(lane.info.key, lane));
    const updatedSwimlaneParents = swimlaneParents.map((parent)=>{
        const laneKey = parent.id.replace(/^swimlane-/, "");
        const lane = laneMetrics.get(laneKey);
        if (!lane) return parent;
        return {
            ...parent,
            position: {
                x: 0,
                y: lane.yOffset
            },
            data: {
                ...parent.data,
                width: swimlaneWidth,
                height: lane.height
            },
            style: {
                ...parent.style,
                width: swimlaneWidth,
                height: lane.height
            }
        };
    });
    // Step 7: Position children (parentId already set during assembly).
    const updatedChildren = positionChildNodes(laneDimensions, elkPositions, contentStartX, globalMinX);
    // Step 8: Position top-level nodes (e.g. start node) vertically centered.
    const updatedTopLevel = topLevelNodes.map((node)=>{
        const elkPos = elkPositions.get(node.id);
        if (!elkPos) return node;
        const { height } = getNodeDimensions(node);
        const centerY = totalSwimlaneHeight / 2 - height / 2;
        return {
            ...node,
            position: {
                x: contentStartX + (elkPos.x - globalMinX),
                y: centerY
            }
        };
    });
    // Step 9: Sort so swimlane parents come before their children (ReactFlow requirement).
    const finalNodes = [
        ...updatedSwimlaneParents,
        ...updatedChildren,
        ...updatedTopLevel
    ];
    finalNodes.sort((a, b)=>{
        const aIsSwimlane = a.type === "flowSwimlane" ? 0 : 1;
        const bIsSwimlane = b.type === "flowSwimlane" ? 0 : 1;
        return aIsSwimlane - bIsSwimlane;
    });
    return {
        nodes: finalNodes,
        edges
    };
}
/**
 * Group nodes by their ELK layer (based on X position).
 * Nodes at similar X positions are considered to be in the same layer.
 * Uses a bucket size to group nodes that are close together.
 */ const LAYER_BUCKET_SIZE = NODE_WIDTH; // Nodes within this X range are considered same layer
function groupByElkLayer(laneNodes, elkPositions) {
    const layerGroups = new Map();
    laneNodes.forEach((node)=>{
        const pos = elkPositions.get(node.id);
        // Round X to bucket size to group nearby nodes together
        const layerKey = pos ? Math.round(pos.x / LAYER_BUCKET_SIZE) * LAYER_BUCKET_SIZE : 0;
        if (!layerGroups.has(layerKey)) {
            layerGroups.set(layerKey, []);
        }
        layerGroups.get(layerKey)?.push(node);
    });
    return layerGroups;
}
/**
 * Simple ELK layout fallback (no swimlanes)
 */ async function applySimpleElkLayout(nodes, edges) {
    const elkNodes = nodes.map((node)=>{
        const { width, height } = getNodeDimensions(node);
        return {
            id: node.id,
            width,
            height
        };
    });
    const elkEdges = edges.map((edge)=>({
            id: edge.id,
            sources: [
                edge.source
            ],
            targets: [
                edge.target
            ]
        }));
    const elkGraph = {
        id: "root",
        layoutOptions: {
            "elk.algorithm": "layered",
            "elk.direction": "RIGHT",
            "elk.layered.spacing.nodeNodeBetweenLayers": String(LAYER_SPACING),
            "elk.layered.spacing.nodeNode": "120",
            "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
            "elk.layered.crossingMinimization.greedySwitch.type": "TWO_SIDED",
            "elk.layered.nodePlacement.bk.edgeStraightening": "IMPROVE_STRAIGHTNESS"
        },
        children: elkNodes,
        edges: elkEdges
    };
    const layoutedGraph = await elk.layout(elkGraph);
    const nodeMap = new Map();
    nodes.forEach((node)=>nodeMap.set(node.id, node));
    const layoutedNodes = (layoutedGraph.children || []).map((elkNode)=>{
        const originalNode = nodeMap.get(elkNode.id);
        if (!originalNode) return null;
        return {
            ...originalNode,
            position: {
                x: elkNode.x || 0,
                y: elkNode.y || 0
            }
        };
    }).filter((node)=>node !== null);
    return {
        nodes: layoutedNodes,
        edges
    };
}

//# sourceMappingURL=layout.js.map